import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from './msalConfig';
import { jsPDF } from 'jspdf';
import { useProcedures } from './centcom/useProcedures';
import { useRoles } from './centcom/useRoles';
import { useSortieRuns } from './centcom/useSortieRuns';
import ProceduresList from './centcom/ProceduresList';
import ProcedureDetail from './centcom/ProcedureDetail';
import SortieRunner from './centcom/SortieRunner';
import './centcom/Sortie.css';

const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const LIBRARY_NAME = 'Family Vault';
const GRAPH = 'https://graph.microsoft.com/v1.0';

// 8.5 x 11 in points
const PAGE_W = 612;
const PAGE_H = 792;

// --- Image helpers ---

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve({ img, dataUrl: reader.result });
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function autoCleanImage(img) {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i], g = d[i + 1], b = d[i + 2];
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    if (lum > 180) {
      d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
    } else {
      const factor = 1.6;
      const mid = 128;
      d[i]     = Math.min(255, Math.max(0, mid + (r - mid) * factor));
      d[i + 1] = Math.min(255, Math.max(0, mid + (g - mid) * factor));
      d[i + 2] = Math.min(255, Math.max(0, mid + (b - mid) * factor));
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/jpeg', 0.92);
}

function fitImageToPage(img) {
  const iw = img.width, ih = img.height;
  const margin = 18;
  const aw = PAGE_W - margin * 2, ah = PAGE_H - margin * 2;
  const scale = Math.min(aw / iw, ah / ih, 1);
  const w = iw * scale, h = ih * scale;
  const x = (PAGE_W - w) / 2, y = (PAGE_H - h) / 2;
  return { x, y, w, h };
}

function buildPdf(pages, clean) {
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
  pages.forEach((page, i) => {
    if (i > 0) pdf.addPage('letter');
    const src = clean ? autoCleanImage(page.img) : page.dataUrl;
    const { x, y, w, h } = fitImageToPage(page.img);
    pdf.addImage(src, 'JPEG', x, y, w, h);
  });
  return pdf.output('blob');
}

function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'svg'].includes(ext)) return 'img';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'doc';
  if (['xls', 'xlsx'].includes(ext)) return 'xls';
  if (['ppt', 'pptx'].includes(ext)) return 'ppt';
  return 'file';
}

const ICON_LABELS = { img: 'IMG', pdf: 'PDF', doc: 'DOC', xls: 'XLS', ppt: 'PPT', file: 'FILE' };
const ICON_COLORS = { img: '#2e7d32', pdf: '#c62828', doc: '#1565c0', xls: '#2e7d32', ppt: '#d84315', file: '#616161' };

// --- Component ---

function DocumentVault() {
  const { instance, accounts } = useMsal();
  const [driveId, setDriveId] = useState(null);
  const [pathStack, setPathStack] = useState([]); // [{id, name}]
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // upload
  const [uploadMode, setUploadMode] = useState(null);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileToUpload, setFileToUpload] = useState(null); // raw File or Blob
  const [fileType, setFileType] = useState('');

  // capture as PDF
  const [pdfPages, setPdfPages] = useState([]); // [{img, dataUrl, file}]
  const [autoClean, setAutoClean] = useState(true);

  // share
  const [shareLink, setShareLink] = useState(null);

  // vault tab: documents or procedures
  const [vaultTab, setVaultTab] = useState('documents');
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [creatingProcedure, setCreatingProcedure] = useState(false);
  const [activeSortie, setActiveSortie] = useState(null); // { procedure, run }
  const [newProcName, setNewProcName] = useState('');
  const [newProcCategory, setNewProcCategory] = useState('');
  const [newProcDesc, setNewProcDesc] = useState('');

  const driveIdRef = useRef(null);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  const userEmail = accounts[0]?.username || '';
  const procHook = useProcedures(getToken, userEmail);
  const { roles } = useRoles(getToken, userEmail);
  const sortieHook = useSortieRuns(getToken, userEmail);

  const getDriveId = useCallback(async (token) => {
    if (driveIdRef.current) return driveIdRef.current;

    const siteRes = await fetch(
      `${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!siteRes.ok) {
      const body = await siteRes.text();
      throw new Error(`Failed to resolve site (${siteRes.status}): ${body}`);
    }
    const site = await siteRes.json();

    const drivesRes = await fetch(
      `${GRAPH}/sites/${site.id}/drives?$select=name,id&$top=100`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!drivesRes.ok) {
      const body = await drivesRes.text();
      throw new Error(`Failed to query drives (${drivesRes.status}): ${body}`);
    }
    const drivesData = await drivesRes.json();
    const allDrives = drivesData.value || [];
    const target = LIBRARY_NAME.toLowerCase();
    const drive = allDrives.find(d => d.name.toLowerCase() === target);
    if (!drive) {
      const names = allDrives.map(d => `"${d.name}"`).join(', ');
      throw new Error(`Library "${LIBRARY_NAME}" not found. Available: ${names}`);
    }
    driveIdRef.current = drive.id;
    setDriveId(drive.id);
    return drive.id;
  }, []);

  const currentFolderId = pathStack.length > 0 ? pathStack[pathStack.length - 1].id : null;

  const fetchItems = useCallback(async (folderId) => {
    try {
      setError(null);
      setLoading(true);
      const token = await getToken();
      const did = await getDriveId(token);
      const url = folderId
        ? `${GRAPH}/drives/${did}/items/${folderId}/children?$top=500`
        : `${GRAPH}/drives/${did}/root/children?$top=500`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to load folder (${res.status}): ${body}`);
      }
      const data = await res.json();
      const sorted = (data.value || []).sort((a, b) => {
        if (a.folder && !b.folder) return -1;
        if (!a.folder && b.folder) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      setItems(sorted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, getDriveId]);

  useEffect(() => {
    fetchItems(currentFolderId);
  }, [fetchItems, currentFolderId]);

  // --- Navigation ---

  function openFolder(item) {
    setSearchResults(null);
    setSearchQuery('');
    setPathStack(prev => [...prev, { id: item.id, name: item.name }]);
  }

  function goToRoot() {
    setSearchResults(null);
    setSearchQuery('');
    setPathStack([]);
  }

  function goToBreadcrumb(index) {
    setSearchResults(null);
    setSearchQuery('');
    setPathStack(prev => prev.slice(0, index + 1));
  }

  // --- Search ---

  async function handleSearch(e) {
    e.preventDefault();
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setSearching(true);
    setError(null);
    try {
      const token = await getToken();
      const did = await getDriveId(token);
      const res = await fetch(
        `${GRAPH}/drives/${did}/root/search(q='${encodeURIComponent(searchQuery.trim())}')`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Search failed (${res.status})`);
      const data = await res.json();
      setSearchResults((data.value || []).filter(i => !i.folder));
    } catch (err) {
      setError(err.message);
    } finally {
      setSearching(false);
    }
  }

  function clearSearch() {
    setSearchQuery('');
    setSearchResults(null);
  }

  // --- File open ---

  function openFile(item) {
    const url = item.webUrl || item['@microsoft.graph.downloadUrl'];
    if (url) window.open(url, '_blank');
  }

  // --- Share ---

  async function handleShare(item) {
    setError(null);
    try {
      const token = await getToken();
      const did = driveIdRef.current;
      const res = await fetch(
        `${GRAPH}/drives/${did}/items/${item.id}/createLink`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'view', scope: 'organization' }),
        }
      );
      if (!res.ok) throw new Error(`Failed to create share link (${res.status})`);
      const data = await res.json();
      const link = data.link?.webUrl;
      if (link) {
        setShareLink(link);
        if (navigator.clipboard) navigator.clipboard.writeText(link).catch(() => {});
      }
    } catch (err) {
      setError(err.message);
    }
  }

  // --- Create folder ---

  async function handleCreateFolder() {
    const folderName = prompt('New folder name:');
    if (!folderName?.trim()) return;
    setError(null);
    try {
      const token = await getToken();
      const did = driveIdRef.current;
      const parentUrl = currentFolderId
        ? `${GRAPH}/drives/${did}/items/${currentFolderId}/children`
        : `${GRAPH}/drives/${did}/root/children`;
      const res = await fetch(parentUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: folderName.trim(),
          folder: {},
          '@microsoft.graph.conflictBehavior': 'fail',
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to create folder (${res.status}): ${body}`);
      }
      await fetchItems(currentFolderId);
    } catch (err) {
      setError(err.message);
    }
  }

  // --- Upload modes ---

  function resetUpload() {
    setUploadMode(null);
    setShowUploadMenu(false);
    setFileName('');
    setFileToUpload(null);
    setFileType('');
    setPdfPages([]);
    setAutoClean(true);
  }

  // Capture Photo
  function startCapturePhoto() {
    setUploadMode('photo');
    setShowUploadMenu(false);
  }

  async function onPhotoCaptured(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileToUpload(file);
    setFileType(file.type);
    const base = file.name.replace(/\.[^.]+$/, '');
    setFileName(base);
  }

  // Capture as PDF (single page)
  function startCapturePdf() {
    setUploadMode('pdf');
    setPdfPages([]);
    setAutoClean(true);
    setShowUploadMenu(false);
  }

  async function onPdfPhotoCaptured(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const { img, dataUrl } = await loadImage(file);
    setPdfPages([{ img, dataUrl, file }]);
    const base = file.name.replace(/\.[^.]+$/, '');
    setFileName(base);
  }

  // Multi-Page Scan
  function startMultiScan() {
    setUploadMode('scan');
    setPdfPages([]);
    setAutoClean(true);
    setFileName('');
    setShowUploadMenu(false);
  }

  async function onScanPageCaptured(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const { img, dataUrl } = await loadImage(file);
    setPdfPages(prev => [...prev, { img, dataUrl, file }]);
    if (!fileName) {
      const base = file.name.replace(/\.[^.]+$/, '');
      setFileName(base);
    }
    e.target.value = '';
  }

  function removeScanPage(index) {
    setPdfPages(prev => prev.filter((_, i) => i !== index));
  }

  function moveScanPage(index, direction) {
    setPdfPages(prev => {
      const arr = [...prev];
      const newIdx = index + direction;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      [arr[index], arr[newIdx]] = [arr[newIdx], arr[index]];
      return arr;
    });
  }

  // Screenshot
  function startScreenshot() {
    setUploadMode('screenshot');
    setShowUploadMenu(false);
  }

  async function onScreenshotFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileToUpload(file);
    setFileType(file.type);
    const base = file.name.replace(/\.[^.]+$/, '');
    setFileName(base);
  }

  async function onPaste(e) {
    const clipItems = e.clipboardData?.items;
    if (!clipItems) return;
    for (const ci of clipItems) {
      if (ci.type.startsWith('image/')) {
        const file = ci.getAsFile();
        if (file) {
          setFileToUpload(file);
          setFileType(file.type);
          setFileName('screenshot');
          break;
        }
      }
    }
  }

  // Upload File (standard picker)
  function startUploadFile() {
    setUploadMode('file');
    setShowUploadMenu(false);
  }

  function onFileSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileToUpload(file);
    setFileType(file.type);
    const base = file.name.replace(/\.[^.]+$/, '');
    setFileName(base);
  }

  // --- Upload execution ---

  function getExtension() {
    if (uploadMode === 'pdf' || uploadMode === 'scan') return '.pdf';
    if (uploadMode === 'screenshot') {
      if (fileType === 'image/png') return '.png';
      if (fileType === 'image/jpeg') return '.jpg';
      return '.png';
    }
    if (uploadMode === 'photo') {
      if (fileType === 'image/png') return '.png';
      if (fileType === 'image/heic') return '.heic';
      return '.jpg';
    }
    if (fileToUpload?.name) {
      const m = fileToUpload.name.match(/\.[^.]+$/);
      return m ? m[0] : '';
    }
    return '';
  }

  async function executeUpload() {
    if (!fileName.trim()) return;
    setUploading(true);
    setError(null);
    try {
      const token = await getToken();
      const did = driveIdRef.current;
      let blob, contentType;
      const ext = getExtension();
      const fullName = fileName.trim() + ext;

      if (uploadMode === 'pdf') {
        blob = buildPdf(pdfPages, autoClean);
        contentType = 'application/pdf';
      } else if (uploadMode === 'scan') {
        blob = buildPdf(pdfPages, autoClean);
        contentType = 'application/pdf';
      } else {
        blob = fileToUpload;
        contentType = fileType || 'application/octet-stream';
      }

      const parentPath = currentFolderId
        ? `${GRAPH}/drives/${did}/items/${currentFolderId}:/${encodeURIComponent(fullName)}:/content`
        : `${GRAPH}/drives/${did}/root:/${encodeURIComponent(fullName)}:/content`;

      const res = await fetch(parentPath, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': contentType,
        },
        body: blob,
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Upload failed (${res.status}): ${body}`);
      }

      resetUpload();
      await fetchItems(currentFolderId);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  // --- Format helpers ---

  function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // --- Render helpers ---

  const isRoot = pathStack.length === 0;
  const displayItems = searchResults !== null ? searchResults : items;
  const folders = displayItems.filter(i => i.folder);
  const files = displayItems.filter(i => !i.folder);
  const canUpload = uploadMode === 'photo' || uploadMode === 'screenshot' || uploadMode === 'file'
    ? fileToUpload && fileName.trim()
    : (uploadMode === 'pdf' || uploadMode === 'scan') ? pdfPages.length > 0 && fileName.trim()
    : false;

  // --- JSX ---

  return (
    <div className="Vault">
      <h1 className="Vault-title">Document Vault</h1>

      <div className="Vault-tabs">
        <button className={`Vault-tab${vaultTab === 'documents' ? ' Vault-tab--active' : ''}`} onClick={() => { setVaultTab('documents'); setSelectedProcedure(null); }}>Documents</button>
        <button className={`Vault-tab${vaultTab === 'procedures' ? ' Vault-tab--active' : ''}`} onClick={() => setVaultTab('procedures')}>Procedures</button>
      </div>

      {/* PROCEDURES TAB */}
      {vaultTab === 'procedures' && !selectedProcedure && !creatingProcedure && (
        <ProceduresList
          procedures={procHook.procedures}
          roles={roles}
          loading={procHook.loading}
          error={procHook.error}
          onSelect={setSelectedProcedure}
          onCreate={() => setCreatingProcedure(true)}
        />
      )}

      {vaultTab === 'procedures' && creatingProcedure && (
        <div className="ProcCreate">
          <h2>New Procedure</h2>
          <input className="Fitness-input" placeholder="Procedure name" value={newProcName} onChange={e => setNewProcName(e.target.value)} />
          <input className="Fitness-input Fitness-input--small" placeholder="Category (e.g. Business/Insurance)" value={newProcCategory} onChange={e => setNewProcCategory(e.target.value)} />
          <input className="Fitness-input Fitness-input--small" placeholder="Description (optional)" value={newProcDesc} onChange={e => setNewProcDesc(e.target.value)} />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="Fitness-btn" disabled={!newProcName.trim()} onClick={async () => {
              const created = await procHook.createProcedure({ name: newProcName.trim(), category: newProcCategory.trim(), description: newProcDesc.trim(), steps: [] });
              setNewProcName(''); setNewProcCategory(''); setNewProcDesc('');
              setCreatingProcedure(false);
              // Find it in the refreshed list
              const found = procHook.procedures.find(p => p.id === created?.id);
              if (found) setSelectedProcedure(found);
            }}>Create</button>
            <button className="Fitness-btn Fitness-btn--secondary" onClick={() => setCreatingProcedure(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Active Sortie Runner */}
      {activeSortie && (
        <SortieRunner
          procedure={procHook.procedures.find(p => p.id === activeSortie.procedure.id) || activeSortie.procedure}
          run={sortieHook.runs.find(r => r.id === activeSortie.run.id) || activeSortie.run}
          roles={roles}
          onToggleStep={sortieHook.toggleStep}
          onComplete={sortieHook.completeRun}
          onAbandon={sortieHook.abandonRun}
          onBack={() => setActiveSortie(null)}
        />
      )}

      {vaultTab === 'procedures' && selectedProcedure && !activeSortie && (() => {
        const proc = procHook.procedures.find(p => p.id === selectedProcedure.id) || selectedProcedure;
        const activeRun = sortieHook.getActiveRun(proc.id);
        const history = sortieHook.getRunHistory(proc.id);
        return (
          <>
            {/* Sortie action buttons */}
            {proc.steps.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                {activeRun ? (
                  <button className="Fitness-btn" onClick={() => setActiveSortie({ procedure: proc, run: activeRun })}>
                    Resume Sortie ({Object.values(activeRun.stepProgress).filter(s => s.done).length}/{proc.steps.length})
                  </button>
                ) : (
                  <button className="Fitness-btn" onClick={async () => {
                    const created = await sortieHook.launchSortie(proc);
                    const run = sortieHook.runs.find(r => r.id === created?.id) || { id: created?.id, stepProgress: {}, startDate: new Date().toISOString() };
                    setActiveSortie({ procedure: proc, run });
                  }}>Launch Sortie</button>
                )}
              </div>
            )}

            {/* Run history */}
            {history.length > 0 && (
              <div className="Sortie-history">
                <div className="Sortie-history-title">Past Sorties</div>
                {history.slice(0, 5).map(r => (
                  <div key={r.id} className="Sortie-history-item">
                    <span className="Sortie-history-date">{new Date(r.startDate).toLocaleDateString()}</span>
                    <span className={`Sortie-history-status Sortie-history-status--${r.status}`}>{r.status}</span>
                    {r.status === 'Active' && (
                      <button className="Fitness-btn Fitness-btn--small" onClick={() => setActiveSortie({ procedure: proc, run: r })}>Resume</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <ProcedureDetail
              procedure={proc}
              roles={roles}
              onBack={() => setSelectedProcedure(null)}
              onEditStep={procHook.editStep}
              onDeleteStep={procHook.deleteStep}
              onAddStep={procHook.addStep}
              onReorderStep={procHook.reorderStep}
              onDelete={procHook.deleteProcedure}
              onEdit={procHook.updateProcedure}
              onGetChangeLog={procHook.getChangeLog}
            />
          </>
        );
      })()}

      {/* DOCUMENTS TAB */}
      {vaultTab !== 'procedures' && error && <p className="Vault-error">{error}</p>}

      {vaultTab === 'documents' && shareLink && (
        <div className="Vault-share-banner">
          <span>Link copied to clipboard!</span>
          <input className="Vault-share-url" readOnly value={shareLink} onFocus={e => e.target.select()} />
          <button className="Vault-share-close" onClick={() => setShareLink(null)}>&times;</button>
        </div>
      )}

      {vaultTab === 'documents' && <>
      {/* Breadcrumbs */}
      <nav className="Vault-breadcrumbs">
        <button className="Vault-crumb" onClick={goToRoot}>Vault</button>
        {pathStack.map((crumb, i) => (
          <span key={crumb.id}>
            <span className="Vault-crumb-sep">/</span>
            <button
              className="Vault-crumb"
              onClick={() => i < pathStack.length - 1 ? goToBreadcrumb(i) : null}
              disabled={i === pathStack.length - 1}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </nav>

      {/* Search */}
      <form className="Vault-search" onSubmit={handleSearch}>
        <input
          className="Vault-search-input"
          type="text"
          placeholder="Search files..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <button className="Vault-search-btn" type="submit" disabled={searching}>
          {searching ? '...' : 'Search'}
        </button>
        {searchResults !== null && (
          <button className="Vault-search-clear" type="button" onClick={clearSearch}>Clear</button>
        )}
      </form>

      {/* Upload menu */}
      {!uploadMode && searchResults === null && (
        <div className="Vault-upload-area">
          <button className="Vault-upload-toggle" onClick={() => setShowUploadMenu(!showUploadMenu)}>
            + Upload
          </button>
          <button className="Vault-newfolder-btn" onClick={handleCreateFolder}>
            + New Folder
          </button>
          {showUploadMenu && (
            <div className="Vault-upload-menu">
              <button onClick={startCapturePhoto}>Capture Photo</button>
              <button onClick={startCapturePdf}>Capture as PDF</button>
              <button onClick={startMultiScan}>Capture Multi-Page PDF</button>
              <button onClick={startScreenshot}>Screenshot</button>
              <button onClick={startUploadFile}>Upload File</button>
            </div>
          )}
        </div>
      )}

      {/* Upload modal */}
      {uploadMode && (
        <div className="Vault-upload-modal">
          <div className="Vault-upload-header">
            <strong>
              {uploadMode === 'photo' && 'Capture Photo'}
              {uploadMode === 'pdf' && 'Capture as PDF'}
              {uploadMode === 'scan' && 'Capture Multi-Page PDF'}
              {uploadMode === 'screenshot' && 'Screenshot'}
              {uploadMode === 'file' && 'Upload File'}
            </strong>
            <button className="Vault-upload-cancel" onClick={resetUpload}>&times;</button>
          </div>

          {/* Capture Photo */}
          {uploadMode === 'photo' && (
            <div className="Vault-upload-body">
              <input type="file" accept="image/*" capture="environment" onChange={onPhotoCaptured} />
              {fileToUpload && <p className="Vault-upload-ready">Photo ready</p>}
            </div>
          )}

          {/* Capture as PDF */}
          {uploadMode === 'pdf' && (
            <div className="Vault-upload-body">
              {pdfPages.length === 0 && (
                <input type="file" accept="image/*" capture="environment" onChange={onPdfPhotoCaptured} />
              )}
              {pdfPages.length > 0 && (
                <>
                  <img className="Vault-upload-preview" src={pdfPages[0].dataUrl} alt="Preview" />
                  <label className="Vault-upload-clean-toggle">
                    <input type="checkbox" checked={autoClean} onChange={e => setAutoClean(e.target.checked)} />
                    Auto-clean (whiten background, increase contrast)
                  </label>
                </>
              )}
            </div>
          )}

          {/* Multi-Page Scan */}
          {uploadMode === 'scan' && (
            <div className="Vault-upload-body">
              <input type="file" accept="image/*" capture="environment" onChange={onScanPageCaptured} />
              {pdfPages.length > 0 && (
                <>
                  <div className="Vault-scan-pages">
                    {pdfPages.map((page, i) => (
                      <div key={i} className="Vault-scan-page">
                        <img src={page.dataUrl} alt={`Page ${i + 1}`} />
                        <span className="Vault-scan-page-num">{i + 1}</span>
                        <div className="Vault-scan-page-actions">
                          <button onClick={() => moveScanPage(i, -1)} disabled={i === 0} title="Move up">&uarr;</button>
                          <button onClick={() => moveScanPage(i, 1)} disabled={i === pdfPages.length - 1} title="Move down">&darr;</button>
                          <button onClick={() => removeScanPage(i)} title="Remove">&times;</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <label className="Vault-upload-clean-toggle">
                    <input type="checkbox" checked={autoClean} onChange={e => setAutoClean(e.target.checked)} />
                    Auto-clean (whiten background, increase contrast)
                  </label>
                </>
              )}
              <p className="Vault-scan-hint">{pdfPages.length} page{pdfPages.length !== 1 ? 's' : ''} captured. Take another photo to add a page.</p>
            </div>
          )}

          {/* Screenshot */}
          {uploadMode === 'screenshot' && (
            <div className="Vault-upload-body" onPaste={onPaste} tabIndex={0}>
              <p className="Vault-scan-hint">Paste from clipboard (Ctrl+V / Cmd+V) or pick a file:</p>
              <input type="file" accept="image/*" onChange={onScreenshotFile} />
              {fileToUpload && <p className="Vault-upload-ready">Image ready</p>}
            </div>
          )}

          {/* Upload File */}
          {uploadMode === 'file' && (
            <div className="Vault-upload-body">
              <input type="file" accept="*/*" onChange={onFileSelected} />
              {fileToUpload && <p className="Vault-upload-ready">{fileToUpload.name}</p>}
            </div>
          )}

          {/* Filename + submit */}
          {(fileToUpload || pdfPages.length > 0) && (
            <div className="Vault-upload-footer">
              <label className="Vault-upload-name-label">
                File name:
                <div className="Vault-upload-name-row">
                  <input
                    className="Vault-upload-name"
                    type="text"
                    value={fileName}
                    onChange={e => setFileName(e.target.value)}
                    required
                  />
                  <span className="Vault-upload-ext">{getExtension()}</span>
                </div>
              </label>
              <button
                className="Vault-upload-submit"
                onClick={executeUpload}
                disabled={!canUpload || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && <p className="Vault-loading">Loading...</p>}

      {/* Search results label */}
      {searchResults !== null && !loading && (
        <p className="Vault-search-label">
          {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
      )}

      {/* Folder tiles (root view) */}
      {!loading && isRoot && searchResults === null && folders.length > 0 && (
        <div className="Vault-tiles">
          {folders.map(item => (
            <button key={item.id} className="Vault-tile" onClick={() => openFolder(item)}>
              <span className="Vault-tile-icon">&#128193;</span>
              <span className="Vault-tile-name">{item.name}</span>
              <span className="Vault-tile-count">{item.folder?.childCount ?? ''} items</span>
            </button>
          ))}
        </div>
      )}

      {/* Subfolder list (non-root) */}
      {!loading && !isRoot && searchResults === null && folders.length > 0 && (
        <div className="Vault-folders">
          {folders.map(item => (
            <button key={item.id} className="Vault-folder-row" onClick={() => openFolder(item)}>
              <span className="Vault-folder-icon">&#128193;</span>
              <span className="Vault-folder-name">{item.name}</span>
              <span className="Vault-folder-count">{item.folder?.childCount ?? ''}</span>
            </button>
          ))}
        </div>
      )}

      {/* Files list */}
      {!loading && files.length > 0 && (
        <ul className="Vault-files">
          {files.map(item => {
            const icon = fileIcon(item.name);
            return (
              <li key={item.id} className="Vault-file">
                <span
                  className="Vault-file-icon"
                  style={{ backgroundColor: ICON_COLORS[icon] }}
                >
                  {ICON_LABELS[icon]}
                </span>
                <div className="Vault-file-info" onClick={() => openFile(item)} style={{ cursor: 'pointer' }}>
                  <span className="Vault-file-name">{item.name}</span>
                  <span className="Vault-file-meta">
                    {formatSize(item.size)} &middot; {formatDate(item.lastModifiedDateTime)}
                  </span>
                </div>
                <button className="Vault-file-share" onClick={() => handleShare(item)} title="Share">
                  &#x1F517;
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Empty state */}
      {!loading && displayItems.length === 0 && (
        <p className="Vault-empty">
          {searchResults !== null ? 'No files found.' : 'This folder is empty.'}
        </p>
      )}
      </>}
    </div>
  );
}

export default DocumentVault;
